import type { ChargeDay, Product } from '@lemontaigu/pms-core';

// « Prestations facturées » — the lines a booking carries on the WHOLE BOOKING,
// not on any room: a breakfast billed per person, the taxe de séjour, the bar.
//
// The booking form prices each room from that room's own charge lines, so these
// were invisible to it, and its product list fell back on the catalogue: a
// breakfast at 11,00 € where Vega billed 9,00 € (CADAY ROUGE, 024667). This is the
// read-only list of what the booking was actually billed, grouped by product and
// price, with the catalogue price beside it when it differs. It is a READING: it
// does not feed the form's total and is never sent back on save.

export interface BilledLine {
  kind: 'product' | 'tax';
  label: string;
  unitCents: number;
  /** Units, summed over the nights (5 people × 3 nights = 15). */
  quantity: number;
  amountCents: number;
  /** The catalogue price, only when it is NOT what was billed. */
  catalogueCents: number | null;
}

export function billedLines(
  charges: readonly ChargeDay[],
  catalogue: readonly Pick<Product, 'id' | 'labelFr' | 'priceCents'>[],
): BilledLine[] {
  const rows = new Map<string, BilledLine>();
  for (const c of charges) {
    if (c.roomStayId !== null || (c.kind !== 'product' && c.kind !== 'tax')) continue;
    const product = c.productId === null ? undefined : catalogue.find((p) => p.id === c.productId);
    const key = `${c.kind}|${c.productId ?? c.labelFr}|${c.unitCents}`;
    const row = rows.get(key);
    if (row !== undefined) {
      row.quantity += c.quantity;
      row.amountCents += c.amountCents;
      continue;
    }
    rows.set(key, {
      kind: c.kind,
      label: product?.labelFr ?? c.labelFr,
      unitCents: c.unitCents,
      quantity: c.quantity,
      amountCents: c.amountCents,
      catalogueCents: c.kind === 'product' && product !== undefined && product.priceCents !== c.unitCents ? product.priceCents : null,
    });
  }
  // products first, the levy last, then by name and price
  return [...rows.values()].sort(
    (a, b) => (a.kind === 'tax' ? 1 : 0) - (b.kind === 'tax' ? 1 : 0) || a.label.localeCompare(b.label, 'fr') || a.unitCents - b.unitCents,
  );
}
